import { AlertIcon, Banner, Box, Flex, Typography } from '@iheartjane/reefer';
import get from 'lodash-es/get';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { hex } from 'wcag-contrast';

import { ColorInput } from '@jane/business-admin/components/premium/ColorInput';

const WCAG_RATIO = 4.5;
const isColorAccessible = (foreground: string, background: string) =>
  hex(foreground, background) >= WCAG_RATIO;
const isHexColor = (color: string) => {
  const hexColorRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/;
  return hexColorRegex.test(color);
};

type COLOR_FIELD = {
  checkAccessibility?: boolean;
  label: string;
  name: string;
};

const ColorPickerInput = ({ label, name, checkAccessibility }: COLOR_FIELD) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const hasError = get(errors, name);

  return (
    <Flex
      mb={16}
      justifyContent="space-between"
      alignItems="center"
      border="grays-light"
      p={8}
      pl={16}
      borderRadius="sm"
    >
      <Flex>
        {hasError && <AlertIcon mr={4} />}
        <Typography>{label}</Typography>
      </Flex>
      <Controller
        name={name}
        control={control}
        rules={{
          validate: {
            hex: (value) => isHexColor(value) || label,
            a11y: (value) => {
              if (!checkAccessibility) {
                return;
              } else {
                return isColorAccessible(value, '#fff') || label;
              }
            },
          },
        }}
        render={({ field, fieldState }) => (
          <ColorInput
            value={field.value ?? '#'}
            onChange={field.onChange}
            onBlur={field.onBlur}
            hasError={!!fieldState.error}
          />
        )}
      />
    </Flex>
  );
};

const COLOR_FIELDS = [
  { label: 'Primary', name: 'colors.primary.main' },
  { label: 'Secondary', name: 'colors.secondary.main' },
  { label: 'Deals', name: 'colors.deals.main', checkAccessibility: true },
  { label: 'Header', name: 'colors.header.main' },
];

const LINEAGE_FIELDS = [
  {
    label: 'Hybrid',
    name: 'colors.lineage.hybrid.main',
    checkAccessibility: true,
  },
  {
    label: 'Indica',
    name: 'colors.lineage.indica.main',
    checkAccessibility: true,
  },
  {
    label: 'Sativa',
    name: 'colors.lineage.sativa.main',
    checkAccessibility: true,
  },
  { label: 'CBD', name: 'colors.lineage.cbd.main', checkAccessibility: true },
];

const buildColorsErrorMessage = (labels: string[], type: 'a11y' | 'hex') => {
  if (labels.length === 0) return '';

  const single =
    type === 'a11y'
      ? 'does not pass accessibility standards'
      : 'is not a valid hex color';
  const plural =
    type === 'a11y'
      ? "don't pass accessibility standards"
      : 'are not valid hex colors';

  if (labels.length === 1) {
    return `${labels[0]} color ${single}.`;
  }

  const last = labels.splice(labels.length - 1, 1);
  return `${labels.join(', ')} and ${last} colors ${plural}.`;
};

export const ColorsSection = () => {
  const {
    formState: { errors },
    trigger,
    watch,
  } = useFormContext();
  const formValues = watch();

  useEffect(() => {
    // Trigger validation when form values intially get set
    // This handles the case where invalid hex colors were previously saved
    trigger();
  }, [JSON.stringify(formValues)]);

  const hexColorErrorMessage = buildColorsErrorMessage(
    [...COLOR_FIELDS, ...LINEAGE_FIELDS]
      .filter(({ name }) => get(errors, name)?.type === 'hex')
      .map(({ label }) => label),
    'hex'
  );

  const a11yColorErrorMessage = buildColorsErrorMessage(
    [...COLOR_FIELDS, ...LINEAGE_FIELDS]
      .filter(({ name }) => get(errors, name)?.type === 'a11y')
      .map(({ label }) => label),
    'a11y'
  );

  return (
    <Box pb={24}>
      {hexColorErrorMessage && (
        <Banner
          mb={24}
          icon={<AlertIcon />}
          label={hexColorErrorMessage}
          variant="error"
        />
      )}
      {a11yColorErrorMessage && (
        <Banner
          mb={24}
          icon={<AlertIcon />}
          label={a11yColorErrorMessage}
          variant="error"
        />
      )}
      <Typography variant="body-bold">Theme</Typography>
      <Typography variant="mini" color="grays-mid" mb={8}>
        Used across categories and buttons
      </Typography>
      {COLOR_FIELDS.map((color) => (
        <ColorPickerInput key={color.name} {...color} />
      ))}
      <Typography variant="body-bold" mt={8}>
        Lineage
      </Typography>
      <Typography variant="mini" color="grays-mid" mb={8}>
        Strain type accents
      </Typography>
      {LINEAGE_FIELDS.map((lineage) => (
        <ColorPickerInput key={lineage.name} {...lineage} />
      ))}
    </Box>
  );
};
